import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  colors,
  Grid,
  makeStyles,
  Typography,
  useTheme,
} from "@material-ui/core";
import { DataContext } from "../DataContext";
import ComplexSelect from "./ComplexSelect";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import { formatDate, formatDateDash } from "../utils/dateUtils";
import DateFnsUtils from "@date-io/date-fns";
import {
  CloseRounded,
  HighlightOffRounded,
  LaunchRounded,
  SearchRounded,
  TuneRounded,
} from "@material-ui/icons";
import { ColorValues } from "../theme/colors";
import { DateFormats } from "../utils/constants";
import axios from "axios";
import fileDownload from "js-file-download";
import { getError } from "../utils/functions";

const useStyles = makeStyles((theme) => {
  let color = ColorValues.GREY;
  return {
    filterItemBadge: {
      display: "inline-block",
      position: "relative",
      borderRadius: "3px",
      padding: "4px 8px",
      margin: "8px 0 8px 16px",
      fontSize: "12px",
      fontWeight: 400,
      letterSpacing: "1px",
      cursor: "default",
      whiteSpace: "nowrap",
      color: color && colors[color][500],
      backgroundColor: color && colors[color][500] + "25",
      "&:hover": {
        backgroundColor: color && colors[color][500] + "35",
      },
      "&:hover>button": {
        display: "inline-block",
      },
    },
    badgeCloseBtn: {
      display: "none",
      position: "absolute",
      right: "-8px",
      top: "-8px",
      margin: 0,
      padding: 0,
      zIndex: 3,
      width: "20px",
      height: "20px",
      borderRadius: "50%",
      backgroundColor: "transparent",
      color: theme.palette.text.secondary,
    },
  };
});

const FilterItemBadge = ({ children, onClose = () => {} }) => {
  const classes = useStyles();
  return (
    <div className={classes.filterItemBadge}>
      {children}
      <ButtonBase
        className={classes.badgeCloseBtn}
        disableRipple
        disableTouchRipple
        onClick={onClose}
      >
        <HighlightOffRounded style={{ fontSize: "18px" }} />
      </ButtonBase>
    </div>
  );
};

const allOpt = { id: "all", value: "All" };
const helperTextStyle = { style: { position: "absolute", bottom: "-22px" } };

const QuickEntryFilter = ({
  staffFilter,
  setStaffFilter,
  dateRange,
  setDateRange,
  hideStaffFilter,
  isFiltering,
  setCurrentPage,
  setPageSize,
  personSid,
  caseSid,
  type = "time",
  hideExportBtn = false,
  isLoading,
}) => {
  const { staffList, alertError } = useContext(DataContext);
  const theme = useTheme();

  const [_staffFilter, _setStaffFilter] = useState(staffFilter);
  const [_dateRange, _setDateRange] = useState(dateRange);
  const [isExporting, setIsExporting] = useState(false);

  const downloadCsvFile = () => {
    let link = `/view-entries/export-to-csv?type=${type}&showArchivedData=false&caseSid=${
      caseSid || "all"
    }&personSid=${personSid || (_staffFilter || allOpt).id}`;
    if (_dateRange[0]) {
      link += `&fromDate=${_dateRange[0]}`;
    }
    if (_dateRange[1]) {
      link += `&toDate=${_dateRange[1]}`;
    }
    setIsExporting(true);
    axios
      .get(link, { responseType: "arraybuffer" })
      .then((res) => {
        fileDownload(
          res.data,
          `${type === "time" ? "time" : "expense"}-entries.csv`,
          "text/csv"
        );
        setIsExporting(false);
      })
      .catch((err) => {
        setIsExporting(false);
        alertError(getError(err, "Error! Failed to download CSV file."));
      });
  };

  const goSearchByFilter = () => {
    if (_dateRange[0] !== "Invalid date" && _dateRange[1] !== "Invalid date") {
      setDateRange(_dateRange);
      setStaffFilter(_staffFilter || allOpt);
      _setStaffFilter(_staffFilter || allOpt);
      if (
        _dateRange[0] ||
        _dateRange[1] ||
        (_staffFilter && _staffFilter.id !== "all")
      ) {
        setCurrentPage && setCurrentPage(1);
        setPageSize && setPageSize(10000);
      } else {
        setPageSize && setPageSize(10);
      }
    } else {
      alertError("Invalid date range selection");
    }
  };

  const goExportToCsvByFilter = () => {
    if (_dateRange[0] !== "Invalid date" && _dateRange[1] !== "Invalid date") {
      _setStaffFilter(_staffFilter || allOpt);
      downloadCsvFile();
    } else {
      alertError("Invalid date range selection");
    }
  };

  const clearFilters = (e) => {
    e.preventDefault();
    _setDateRange([null, null]);
    setDateRange([null, null]);
    _setStaffFilter(allOpt);
    setStaffFilter(allOpt);
    setCurrentPage && setCurrentPage(1);
    setPageSize && setPageSize(10);
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Box
        className="QuickEntryFilter"
        style={{
          borderRadius: "5px",
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: theme.palette.divider,
          padding: theme.spacing(2),
          paddingBottom: theme.spacing(1),
          marginBottom: theme.spacing(1.5),
        }}
      >
        <Grid
          container
          spacing={3}
          style={{
            marginBottom: theme.spacing(1.5),
            alignItems: "flex-end",
          }}
        >
          <Grid item xs={6} md={3}>
            <KeyboardDatePicker
              fullWidth
              margin="none"
              placeholder="MM/dd/yyyy"
              format="dd-MMM-yyyy"
              label="From Date"
              value={_dateRange[0] ? new Date(_dateRange[0]) : null}
              onChange={(d) =>
                _setDateRange([formatDateDash(d), _dateRange[1]])
              }
              FormHelperTextProps={helperTextStyle}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <KeyboardDatePicker
              fullWidth
              margin="none"
              placeholder="MM/dd/yyyy"
              format="dd-MMM-yyyy"
              label="To Date"
              value={_dateRange[1] ? new Date(_dateRange[1]) : null}
              onChange={(d) =>
                _setDateRange([_dateRange[0], formatDateDash(d)])
              }
              FormHelperTextProps={helperTextStyle}
            />
          </Grid>
          {!hideStaffFilter && (
            <Grid item xs={8} sm={6} md={3}>
              <ComplexSelect
                fullWidth
                label="By Lawyer"
                options={[allOpt, ...staffList]}
                value={_staffFilter}
                onChange={(e) => _setStaffFilter(e.target.value)}
              />
            </Grid>
          )}
          <Grid item xs={12} md={3}>
            <div className="d-flex align-items-center">
              <Button
                color="primary"
                variant="contained"
                onClick={goSearchByFilter}
                startIcon={<SearchRounded />}
                disabled={isLoading}
              >
                Search
              </Button>
              {type === "time" && !hideExportBtn && (
                <Button
                  color="primary"
                  onClick={goExportToCsvByFilter}
                  startIcon={<LaunchRounded fontSize="small" />}
                  style={{ marginLeft: "16px", padding: "5px 12px" }}
                  disabled={
                    isExporting ||
                    !(
                      caseSid ||
                      personSid ||
                      (_staffFilter && _staffFilter.id !== "all") ||
                      _dateRange[0] ||
                      _dateRange[1]
                    )
                  }
                >
                  Export
                </Button>
              )}
            </div>
          </Grid>
        </Grid>
        {isFiltering && (
          <div className="d-flex flex-wrap align-items-center">
            <Typography
              className="d-flex align-items-center"
              variant="body2"
              color="textSecondary"
              style={{ marginLeft: "8px" }}
            >
              <TuneRounded fontSize="small" style={{ marginRight: "4px" }} />
              <span>Filters:</span>
            </Typography>
            {(dateRange[0] || dateRange[1]) && (
              <FilterItemBadge
                onClose={() => {
                  _setDateRange([null, null]);
                  setDateRange([null, null]);
                  if (!staffFilter || staffFilter.id === "all") {
                    setCurrentPage && setCurrentPage(1);
                    setPageSize && setPageSize(10);
                  }
                }}
              >
                {dateRange[0]
                  ? formatDate(dateRange[0], DateFormats.MEDIUM)
                  : "Oldest"}{" "}
                -{" "}
                {dateRange[1]
                  ? formatDate(dateRange[1], DateFormats.MEDIUM)
                  : "Newest"}
              </FilterItemBadge>
            )}
            {staffFilter && staffFilter.id !== "all" && (
              <FilterItemBadge
                onClose={() => {
                  _setStaffFilter(allOpt);
                  setStaffFilter(allOpt);
                  if (!dateRange[0] && !dateRange[1]) {
                    setCurrentPage && setCurrentPage(1);
                    setPageSize && setPageSize(10);
                  }
                }}
              >
                {staffFilter.value}
              </FilterItemBadge>
            )}
            <Button
              className="clear-filter-btn"
              color="secondary"
              size="small"
              onClick={clearFilters}
              startIcon={<CloseRounded fontSize="small" />}
            >
              Clear
            </Button>
          </div>
        )}
      </Box>
    </MuiPickersUtilsProvider>
  );
};

export default QuickEntryFilter;
